import { mentionToken } from "@/api/tickets";

const mentionTokenPattern = /@\{([0-9a-fA-F-]{36})\}/g;
const mentionLeadingBoundaryPattern = /[\s([{"'，。！？、；：,.;!?]/u;
const mentionTrailingBoundaryPattern = /[\s)\]}"'，。！？、；：,.;!?]/u;

/** 编辑器里一次具体的提及；位置是显示文本 `@姓名` 在正文中的半开区间。 */
export type TicketMentionOccurrence = {
  agentId: string;
  displayName: string;
  end: number;
  start: number;
};

export type TicketMentionTokenMatch = {
  agentId: string;
  end: number;
  start: number;
};

export type DisplayMentionMatch = {
  displayName: string;
  end: number;
  start: number;
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** 按已知显示名寻找完整的 `@姓名`，姓名可以包含空格或正则特殊字符。 */
export function findDisplayMentionMatches(content: string, displayNames: Iterable<string>): DisplayMentionMatch[] {
  const alternatives = [...displayNames]
    .filter(Boolean)
    .sort((left, right) => right.length - left.length)
    .map(escapeRegExp);
  if (alternatives.length === 0) return [];

  const pattern = new RegExp(`(^|[\\s([{"'，。！？、；：,.;!?])@(${alternatives.join("|")})(?=$|[\\s)\\]}"'，。！？、；：,.;!?])`, "gu");

  return [...content.matchAll(pattern)].map((match) => {
    const prefixLength = match[1]?.length ?? 0;
    const displayName = match[2] ?? "";
    const start = (match.index ?? 0) + prefixLength;
    return { displayName, start, end: start + displayName.length + 1 };
  });
}

function isValidMentionOccurrence(content: string, occurrence: TicketMentionOccurrence): boolean {
  if (occurrence.start < 0 || occurrence.end > content.length || occurrence.start >= occurrence.end) return false;
  if (content.slice(occurrence.start, occurrence.end) !== `@${occurrence.displayName}`) return false;

  const leadingCharacter = content[occurrence.start - 1];
  const trailingCharacter = content[occurrence.end];
  const hasValidLeadingBoundary = leadingCharacter === undefined || mentionLeadingBoundaryPattern.test(leadingCharacter);
  const hasValidTrailingBoundary = trailingCharacter === undefined || mentionTrailingBoundaryPattern.test(trailingCharacter);
  return hasValidLeadingBoundary && hasValidTrailingBoundary;
}

/**
 * textarea 每次只产生一段连续变更。用最长公共前后缀定位变更区间：
 * 变更前后的提及平移，碰到变更或失去词边界的提及降级为普通文本。
 */
export function reconcileTicketMentionOccurrences(
  previousContent: string,
  nextContent: string,
  occurrences: readonly TicketMentionOccurrence[],
): TicketMentionOccurrence[] {
  let editStart = 0;
  const sharedPrefixLimit = Math.min(previousContent.length, nextContent.length);
  while (editStart < sharedPrefixLimit && previousContent[editStart] === nextContent[editStart]) editStart += 1;

  let sharedSuffixLength = 0;
  const sharedSuffixLimit = Math.min(previousContent.length - editStart, nextContent.length - editStart);
  while (
    sharedSuffixLength < sharedSuffixLimit &&
    previousContent[previousContent.length - sharedSuffixLength - 1] === nextContent[nextContent.length - sharedSuffixLength - 1]
  ) {
    sharedSuffixLength += 1;
  }

  const previousEditEnd = previousContent.length - sharedSuffixLength;
  const nextEditEnd = nextContent.length - sharedSuffixLength;
  const offset = nextEditEnd - previousEditEnd;

  return occurrences
    .flatMap((occurrence) => {
      if (occurrence.end <= editStart) return [occurrence];
      if (occurrence.start >= previousEditEnd) {
        return [{ ...occurrence, start: occurrence.start + offset, end: occurrence.end + offset }];
      }
      return [];
    })
    .filter((occurrence) => isValidMentionOccurrence(nextContent, occurrence))
    .sort((left, right) => left.start - right.start);
}

/**
 * 把编辑器里逐次记录的 `@显示名` 换成后端 `@{agentId}` token。
 * 身份绑定在每个文本区间上，因此同名成员在同一条消息中也不会互相覆盖。
 */
export function serializeTicketMentions(content: string, occurrences: readonly TicketMentionOccurrence[]): string {
  let cursor = 0;
  let serialized = "";
  for (const occurrence of [...occurrences].sort((left, right) => left.start - right.start)) {
    if (occurrence.start < cursor || !isValidMentionOccurrence(content, occurrence)) continue;
    serialized += content.slice(cursor, occurrence.start) + mentionToken(occurrence.agentId);
    cursor = occurrence.end;
  }
  return serialized + content.slice(cursor);
}

/** 找出正文中的 ID token；展示层用 agentId 取显示名和绑定名片。 */
export function findTicketMentionTokenMatches(content: string): TicketMentionTokenMatch[] {
  return [...content.matchAll(mentionTokenPattern)].map((match) => ({
    agentId: match[1] ?? "",
    start: match.index ?? 0,
    end: (match.index ?? 0) + match[0].length,
  }));
}

/** `@{agentId}` → `@显示名` 的纯文本转换；需要身份交互时应保留 token。 */
export function renderMentionTokens(body: string, nameByAgentId: ReadonlyMap<string, string>): string {
  return body.replace(mentionTokenPattern, (whole, agentId: string) => {
    const displayName = nameByAgentId.get(agentId);
    return displayName ? `@${displayName}` : whole;
  });
}
